/**
 * AdminTemplates Page
 * CRUD interface for managing platform default templates.
 * Admin-only — requires Cognito 'admins' group membership.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  FileText,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Eye,
  Shield,
  ShieldAlert,
  Target,
  GitBranch,
  ClipboardList,
  Layers,
  BookOpen,
  FlaskConical,
  ListChecks,
  Flag,
  LayoutDashboard,
  ArrowLeft,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import { PageLayout } from '@/components';
import { Button, UnauthenticatedPage } from '@/components/ui';
import { MarkdownPreview, DocumentEditor } from '@/components/markdown';
import { templatesApi, DefaultTemplateMetadata, SaveDefaultTemplateRequest } from '@/services/templatesApi';
import { errorMessages } from '@/lib/errors';
import { formatDate as formatDateLocale } from '@/lib/i18n-utils';

// Icon map for template display
const ICON_MAP: Record<string, React.ElementType> = {
  Target,
  GitBranch,
  FileText,
  ClipboardList,
  Layers,
  BookOpen,
  FlaskConical,
  ListChecks,
  Flag,
  LayoutDashboard,
};

const ICON_OPTIONS = Object.entries(ICON_MAP).map(([key, Component]) => ({
  value: key,
  Component,
}));

// Color options
const COLOR_OPTIONS = [
  { value: 'blue', label: 'Blue', bg: 'bg-blue-100', text: 'text-blue-600', ring: 'ring-blue-500' },
  { value: 'emerald', label: 'Green', bg: 'bg-emerald-100', text: 'text-emerald-600', ring: 'ring-emerald-500' },
  { value: 'purple', label: 'Purple', bg: 'bg-purple-100', text: 'text-purple-600', ring: 'ring-purple-500' },
  { value: 'amber', label: 'Amber', bg: 'bg-amber-100', text: 'text-amber-600', ring: 'ring-amber-500' },
  { value: 'rose', label: 'Rose', bg: 'bg-rose-100', text: 'text-rose-600', ring: 'ring-rose-500' },
  { value: 'indigo', label: 'Indigo', bg: 'bg-indigo-100', text: 'text-indigo-600', ring: 'ring-indigo-500' },
  { value: 'slate', label: 'Grey', bg: 'bg-slate-100', text: 'text-slate-600', ring: 'ring-slate-500' },
  { value: 'violet', label: 'Violet', bg: 'bg-violet-100', text: 'text-violet-600', ring: 'ring-violet-500' },
];

// Output format options
const OUTPUT_FORMAT_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'adr', label: 'ADR' },
  { value: 'position-statement', label: 'Position Statement' },
  { value: 'requirements', label: 'Requirements' },
  { value: 'solution-design', label: 'Solution Architecture' },
  { value: 'system-docs', label: 'System Documentation' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'bdd', label: 'BDD Scenarios' },
  { value: 'custom', label: 'Custom' },
];

const getColorClasses = (color: string) => {
  const found = COLOR_OPTIONS.find(c => c.value === color);
  return { bg: found?.bg || 'bg-slate-100', text: found?.text || 'text-slate-600' };
};

interface EditorState {
  isOpen: boolean;
  mode: 'create' | 'edit';
  templateId?: string;
  templateName: string;
  description: string;
  templateStructure: string;
  outputFormat: string;
  color: string;
  icon: string;
  prompt: string;
  reviewMode: 'preview' | 'edit';
  hasUnsavedChanges: boolean;
  originalData: string;
}

const initialEditorState: EditorState = {
  isOpen: false,
  mode: 'create',
  templateName: '',
  description: '',
  templateStructure: '# Template Title\n\n## Section 1\n\nYour content here...\n',
  outputFormat: 'general',
  color: 'slate',
  icon: 'FileText',
  prompt: '',
  reviewMode: 'edit',
  hasUnsavedChanges: false,
  originalData: '',
};

export const AdminTemplates: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: authLoading, isAdmin } = useAuthContext();
  const [templates, setTemplates] = useState<DefaultTemplateMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>(initialEditorState);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Clear action error after 5 seconds
  useEffect(() => {
    if (actionError) {
      const timer = setTimeout(() => setActionError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [actionError]);

  // Fetch templates
  const fetchTemplates = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const data = await templatesApi.listDefaultTemplates();
      setTemplates(data);
    } catch (err) {
      setError(errorMessages.loadDefaultTemplates(err));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Load templates on mount
  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchTemplates();
    }
  }, [isAuthenticated, isAdmin, fetchTemplates]);

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return formatDateLocale(dateString, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Open editor for creating new template
  const handleCreate = () => {
    const defaultStructure = '# Template Title\n\n## Section 1\n\nYour content here...\n';
    setEditor({
      ...initialEditorState,
      isOpen: true,
      mode: 'create',
      templateStructure: defaultStructure,
      reviewMode: 'edit',
      originalData: '',
    });
    setActionError(null);
  };

  // Open editor for editing existing template
  const handleEdit = async (template: DefaultTemplateMetadata) => {
    setActionError(null);
    try {
      // Fetch full template to get templateStructure and prompt
      const full = await templatesApi.getDefaultTemplate(template.templateId);
      const dataSnapshot = JSON.stringify({
        templateName: full.templateName,
        description: full.description,
        templateStructure: full.templateStructure,
        outputFormat: full.outputFormat,
        color: full.color,
        icon: full.icon,
        prompt: full.prompt,
      });
      setEditor({
        isOpen: true,
        mode: 'edit',
        templateId: full.templateId,
        templateName: full.templateName,
        description: full.description || '',
        templateStructure: full.templateStructure || '',
        outputFormat: full.outputFormat || 'general',
        color: full.color || 'slate',
        icon: full.icon || 'FileText',
        prompt: full.prompt || '',
        reviewMode: 'edit',
        hasUnsavedChanges: false,
        originalData: dataSnapshot,
      });
    } catch (err) {
      setActionError(errorMessages.loadDefaultTemplates(err));
    }
  };

  // Handle content edit with unsaved changes tracking
  const handleStructureEdit = useCallback((newContent: string) => {
    setEditor(prev => ({
      ...prev,
      templateStructure: newContent,
      hasUnsavedChanges: true,
    }));
  }, []);

  // Handle closing the editor with unsaved changes confirmation
  const handleCloseEditor = useCallback(() => {
    if (editor.hasUnsavedChanges) {
      if (!window.confirm(t('admin.templates.unsavedConfirm'))) {
        return;
      }
    }
    setEditor(initialEditorState);
  }, [editor.hasUnsavedChanges, t]);

  // Save template (create or update)
  const handleSave = async () => {
    if (!editor.templateName.trim()) {
      setActionError(t('admin.templates.validationTemplateName'));
      return;
    }

    setIsSaving(true);
    setActionError(null);

    try {
      const request: SaveDefaultTemplateRequest = {
        templateName: editor.templateName.trim(),
        description: editor.description.trim(),
        templateStructure: editor.templateStructure,
        outputFormat: editor.outputFormat,
        color: editor.color,
        icon: editor.icon,
        prompt: editor.prompt.trim(),
      };

      if (editor.mode === 'edit' && editor.templateId) {
        await templatesApi.updateDefaultTemplate(editor.templateId, request);
      } else {
        await templatesApi.createDefaultTemplate(request);
      }

      setEditor(initialEditorState);
      await fetchTemplates();
    } catch (err) {
      setActionError(errorMessages.saveDefaultTemplate(err));
    } finally {
      setIsSaving(false);
    }
  };

  // Delete template
  const handleDelete = async (templateId: string) => {
    setActionError(null);
    try {
      await templatesApi.deleteDefaultTemplate(templateId);
      setDeleteConfirm(null);
      await fetchTemplates();
    } catch (err) {
      setActionError(errorMessages.deleteDefaultTemplate(err));
    }
  };

  // Auth loading state
  if (authLoading) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        </div>
      </PageLayout>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <UnauthenticatedPage
        icon={<Shield className="w-8 h-8 text-slate-400" />}
        title={t('admin.templates.unauthTitle')}
        description={t('admin.templates.unauthDescription')}
      />
    );
  }

  // Not admin
  if (!isAdmin) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="text-center py-16">
            <ShieldAlert className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('admin.templates.accessDenied')}</h1>
            <p className="text-slate-600 mb-6">
              {t('admin.templates.noPermission')}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors"
            >
              {t('admin.templates.goHome')}
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Loading templates
  if (isLoading && templates.length === 0) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mr-3" />
            <span className="text-slate-600">{t('admin.templates.loadingTemplates')}</span>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link
                to="/admin"
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                title={t('admin.templates.backToAdmin')}
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FileText className="w-6 h-6 text-indigo-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">{t('admin.templates.title')}</h1>
            </div>
            <p className="text-slate-600 ml-[88px]">
              {t('admin.templates.templateCount', { count: templates.length })}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchTemplates(true)}
              disabled={isRefreshing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {t('admin.templates.refresh')}
            </button>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('admin.templates.newTemplate')}
            </button>
          </div>
        </div>

        {/* Error banner */}
        {(error || actionError) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-red-700">{actionError || error}</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && templates.length === 0 && (
          <div className="text-center py-16 bg-slate-50 rounded-2xl border border-slate-200">
            <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">{t('admin.templates.noTemplatesTitle')}</h2>
            <p className="text-slate-500 mb-6">
              {t('admin.templates.noTemplatesDescription')}
            </p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              {t('admin.templates.createTemplate')}
            </button>
          </div>
        )}

        {/* Template grid */}
        {templates.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(template => {
              const colors = getColorClasses(template.color || 'slate');
              const IconComponent = ICON_MAP[template.icon || ''] || FileText;
              return (
                <div
                  key={template.templateId}
                  className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                >
                  {/* Template header */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${colors.bg}`}>
                      <IconComponent className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{template.templateName}</h3>
                      <p className="text-sm text-slate-500">
                        {OUTPUT_FORMAT_OPTIONS.find(f => f.value === template.outputFormat)?.label || template.outputFormat}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                    {template.description || t('admin.templates.noDescription')}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-400">
                      {t('admin.templates.updatedDate', { date: formatDate(template.updatedAt) })}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEdit(template)}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title={t('admin.templates.editButton')}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(template.templateId)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('admin.templates.deleteButton')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('admin.templates.deleteModalTitle')}</h3>
              <p className="text-slate-600 mb-6">
                {t('admin.templates.deleteModalDescription')}
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  {t('admin.templates.cancel')}
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t('admin.templates.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Template editor modal */}
        {editor.isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              onClick={handleCloseEditor}
            />
            {/* Panel */}
            <div className="relative bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:rounded-2xl shadow-2xl flex flex-col">
              {/* Modal header */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`p-2 rounded-lg ${getColorClasses(editor.color).bg}`}>
                    {(() => {
                      const Icon = ICON_MAP[editor.icon] || FileText;
                      return <Icon className={`w-5 h-5 ${getColorClasses(editor.color).text}`} />;
                    })()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base sm:text-xl font-bold text-slate-900 truncate">
                        {editor.mode === 'create' ? t('admin.templates.editorCreateTitle') : editor.templateName || t('admin.templates.editorEditTitle')}
                      </h3>
                      {editor.hasUnsavedChanges && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          {t('admin.templates.unsaved')}
                        </span>
                      )}
                    </div>
                    {editor.mode === 'edit' && (
                      <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                        {OUTPUT_FORMAT_OPTIONS.find(f => f.value === editor.outputFormat)?.label || 'Custom'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Preview/Edit Toggle */}
                <div className="flex items-center gap-2 mr-2">
                  <div className="flex items-center bg-slate-100 rounded-lg p-1">
                    <button
                      onClick={() => setEditor(prev => ({ ...prev, reviewMode: 'preview' }))}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                        editor.reviewMode === 'preview'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">{t('admin.templates.preview')}</span>
                    </button>
                    <button
                      onClick={() => setEditor(prev => ({ ...prev, reviewMode: 'edit' }))}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                        editor.reviewMode === 'edit'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <Edit3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">{t('admin.templates.edit')}</span>
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleCloseEditor}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal body */}
              <div className="flex-1 overflow-y-auto">
                {editor.reviewMode === 'preview' ? (
                  <div className="px-4 sm:px-6 py-4">
                    <MarkdownPreview content={editor.templateStructure} />
                  </div>
                ) : (
                  <div className="p-4 sm:p-6 space-y-4">
                    {/* Name and Description */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          {t('admin.templates.labelTemplateName')}
                        </label>
                        <input
                          type="text"
                          value={editor.templateName}
                          onChange={e => setEditor(prev => ({ ...prev, templateName: e.target.value, hasUnsavedChanges: true }))}
                          placeholder={t('admin.templates.placeholderTemplateName')}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          {t('admin.templates.labelDescription')}
                        </label>
                        <input
                          type="text"
                          value={editor.description}
                          onChange={e => setEditor(prev => ({ ...prev, description: e.target.value, hasUnsavedChanges: true }))}
                          placeholder={t('admin.templates.placeholderDescription')}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>

                    {/* Output Format and Color */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          {t('admin.templates.labelOutputFormat')}
                        </label>
                        <select
                          value={editor.outputFormat}
                          onChange={e => setEditor(prev => ({ ...prev, outputFormat: e.target.value, hasUnsavedChanges: true }))}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          {OUTPUT_FORMAT_OPTIONS.map(format => (
                            <option key={format.value} value={format.value}>
                              {format.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          {t('admin.templates.labelColor')}
                        </label>
                        <div className="flex items-center gap-2 py-1.5">
                          {COLOR_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => setEditor(prev => ({ ...prev, color: opt.value, hasUnsavedChanges: true }))}
                              className={`w-8 h-8 rounded-full border-2 transition-all ${opt.bg} ${
                                editor.color === opt.value ? 'border-slate-900 scale-110' : 'border-transparent hover:border-slate-300'
                              }`}
                              title={opt.label}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Icon picker */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('admin.templates.labelIcon')}
                      </label>
                      <div className="flex flex-wrap items-center gap-2">
                        {ICON_OPTIONS.map(opt => {
                          const Icon = opt.Component;
                          return (
                            <button
                              key={opt.value}
                              onClick={() => setEditor(prev => ({ ...prev, icon: opt.value, hasUnsavedChanges: true }))}
                              className={`p-2.5 rounded-lg border-2 transition-all ${
                                editor.icon === opt.value
                                  ? 'border-indigo-500 bg-indigo-50 text-indigo-600'
                                  : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
                              }`}
                              title={opt.value}
                            >
                              <Icon className="w-5 h-5" />
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* AI Prompt */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('admin.templates.labelAiPrompt')}
                      </label>
                      <textarea
                        value={editor.prompt}
                        onChange={e => setEditor(prev => ({ ...prev, prompt: e.target.value, hasUnsavedChanges: true }))}
                        placeholder={t('admin.templates.placeholderAiPrompt')}
                        rows={6}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {t('admin.templates.aiPromptHint')}
                      </p>
                    </div>

                    {/* Template Structure (markdown) */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('admin.templates.labelTemplateStructure')}
                      </label>
                      <div className="border border-slate-300 rounded-lg overflow-hidden">
                        <DocumentEditor
                          value={editor.templateStructure}
                          onChange={handleStructureEdit}
                          placeholder={t('admin.templates.placeholderTemplateStructure')}
                          minHeight="min-h-[200px]"
                          enableAiRefine={false}
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {t('admin.templates.templateStructureHint')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal footer */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 bg-slate-50 sm:rounded-b-2xl">
                <button
                  onClick={handleCloseEditor}
                  disabled={isSaving}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  {editor.hasUnsavedChanges ? t('admin.templates.discard') : t('admin.templates.cancel')}
                </button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !editor.templateName.trim()}
                  variant="gradient-emerald"
                  size="md"
                  shape="default"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('admin.templates.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editor.mode === 'create' ? t('admin.templates.createTemplate') : t('admin.templates.saveChanges')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default AdminTemplates;
